// Equipment bands and the tuning derived from them — the shared half of
// the wear/charge design (docs/design/trades.md §9–§13).
//
// The split is load-bearing: the wear and charge INTEGERS are server-only
// and never cross the wire in any form (the concentration-seed bargain);
// what replicates is a BAND — a word — and everything the client needs to
// predict handling derives from that word alone. A server-only movement
// modifier the client could not reproduce would snap the rider several
// times a second, on the body of whoever is already having the worst time,
// which is the same reasoning that put carriage on the wire.
//
// Band vocabularies are per class so one domain's words can never be
// mistaken for another's. No band is `destroyed`, `broken` or `scrapped`,
// and the worst band keeps a non-zero top speed — wear removes
// performance; it never removes the machine (§13). Tests assert this.

import { SKIFF_ACCEL, SKIFF_MAX_SPEED } from "./constants";

/** What a piece of equipment is. Skiff and aetherometer wear from v1; the
 *  ship joined at the space wave on the same rails. */
export const EQUIPMENT_CLASS_IDS = ["skiff", "aetherometer", "ship"] as const;
export type EquipmentClassId = (typeof EQUIPMENT_CLASS_IDS)[number];

/** Skiff wear, best to worst. Words, never numbers. */
export const SKIFF_WEAR_BANDS = ["true", "scuffed", "rough", "sour", "derelict"] as const;
export type SkiffWearBand = (typeof SKIFF_WEAR_BANDS)[number];

/** Aetherometer wear, best to worst. */
export const AETHEROMETER_WEAR_BANDS = ["true", "drifting", "noisy", "dull", "faint"] as const;
export type AetherometerWearBand = (typeof AETHEROMETER_WEAR_BANDS)[number];

/** Ship wear, best to worst — its own vocabulary so one class's words can
 *  never be mistaken for another's. The worst band still flies (§13). */
export const SHIP_WEAR_BANDS = ["true", "weathered", "strained", "laboring", "battered"] as const;
export type ShipWearBand = (typeof SHIP_WEAR_BANDS)[number];

/** Charge, best to worst. `dry` is exactly zero — a label, never a roll. */
export const CHARGE_BANDS = ["full", "good", "low", "dregs", "dry"] as const;
export type ChargeBand = (typeof CHARGE_BANDS)[number];

/** Coerce a replicated string to a skiff wear band; unknown reads `true`. */
export function asSkiffWearBand(value: string): SkiffWearBand {
  return (SKIFF_WEAR_BANDS as readonly string[]).includes(value)
    ? (value as SkiffWearBand)
    : "true";
}

/** Coerce a replicated string to a ship wear band; unknown reads `true`. */
export function asShipWearBand(value: string): ShipWearBand {
  return (SHIP_WEAR_BANDS as readonly string[]).includes(value) ? (value as ShipWearBand) : "true";
}

/** Coerce a replicated string to a charge band; unknown reads `full`. */
export function asChargeBand(value: string): ChargeBand {
  return (CHARGE_BANDS as readonly string[]).includes(value) ? (value as ChargeBand) : "full";
}

/**
 * How a hull at a wear band performs, as factors on the true-hull
 * constants. A worn machine is slower, not stranger (§10): performance
 * falls monotonically, burn rises, and nothing here ever adds randomness,
 * fabricates a failure, or lies about an input.
 */
export interface SkiffTuning {
  /** Factor on SKIFF_MAX_SPEED. Strictly positive at every band. */
  maxSpeedFactor: number;
  /** Factor on SKIFF_ACCEL. */
  accelFactor: number;
  /** Factor on yaw authority (both ends of the speed taper). */
  yawFactor: number;
  /** Factor on charge burned per metre (server-side; shared so the same
   *  table answers "how far will it go" if a panel ever needs the word). */
  burnFactor: number;
}

export const SKIFF_WEAR_TUNING: Record<SkiffWearBand, SkiffTuning> = {
  true: { maxSpeedFactor: 1, accelFactor: 1, yawFactor: 1, burnFactor: 1 },
  scuffed: { maxSpeedFactor: 0.93, accelFactor: 0.9, yawFactor: 0.95, burnFactor: 1.15 },
  rough: { maxSpeedFactor: 0.84, accelFactor: 0.78, yawFactor: 0.85, burnFactor: 1.35 },
  sour: { maxSpeedFactor: 0.72, accelFactor: 0.64, yawFactor: 0.72, burnFactor: 1.6 },
  derelict: { maxSpeedFactor: 0.55, accelFactor: 0.5, yawFactor: 0.6, burnFactor: 2 },
};

/**
 * The tuning a rider actually gets, from the two replicated band words.
 *
 * A dry hull accepts no throttle at any wear band — maxSpeedFactor 0 makes
 * stepSkiff treat the stick as released, so a skiff that runs dry mid-trip
 * coasts to a stop instead of dying mid-air or refusing to have moved.
 * There is no ignition check anywhere: any charge above zero accepts
 * throttle, and dry is a word you read on the machine before boarding.
 */
export function skiffPerformance(wearBand: string, chargeBand: string): SkiffTuning {
  const tuning = SKIFF_WEAR_TUNING[asSkiffWearBand(wearBand)];
  if (asChargeBand(chargeBand) !== "dry") return tuning;
  return { ...tuning, maxSpeedFactor: 0, accelFactor: 0 };
}

/** Effective top speed at a band pair, m/s. */
export function skiffTopSpeed(tuning: SkiffTuning): number {
  return SKIFF_MAX_SPEED * tuning.maxSpeedFactor;
}

/** Effective acceleration at a band pair, m/s². */
export function skiffAccel(tuning: SkiffTuning): number {
  return SKIFF_ACCEL * tuning.accelFactor;
}

/**
 * How a ship hull at a wear band performs — the skiff's contract with a
 * vertical axis: performance falls monotonically, burn rises, nothing
 * fabricates a failure. The worst band keeps a real top speed and a real
 * climb: wear removes performance; it never removes the machine.
 */
export interface ShipTuning extends SkiffTuning {
  /** Factor on the climb rate. Strictly positive at every band. */
  climbFactor: number;
}

export const SHIP_WEAR_TUNING: Record<ShipWearBand, ShipTuning> = {
  true: { maxSpeedFactor: 1, accelFactor: 1, yawFactor: 1, climbFactor: 1, burnFactor: 1 },
  weathered: {
    maxSpeedFactor: 0.93,
    accelFactor: 0.9,
    yawFactor: 0.95,
    climbFactor: 0.92,
    burnFactor: 1.15,
  },
  strained: {
    maxSpeedFactor: 0.84,
    accelFactor: 0.78,
    yawFactor: 0.85,
    climbFactor: 0.82,
    burnFactor: 1.35,
  },
  laboring: {
    maxSpeedFactor: 0.72,
    accelFactor: 0.64,
    yawFactor: 0.72,
    climbFactor: 0.7,
    burnFactor: 1.6,
  },
  battered: {
    maxSpeedFactor: 0.55,
    accelFactor: 0.5,
    yawFactor: 0.6,
    climbFactor: 0.55,
    burnFactor: 2,
  },
};

/**
 * The band-derived half of a ship's tuning. A dry hull accepts no throttle
 * and no climb (stepShip sinks it gently to the ground instead) — dry is a
 * word read on the machine before boarding, exactly as it is for a skiff.
 * The routing/boost shaping on top of this lives in ship.ts.
 */
export function shipPerformance(wearBand: string, chargeBand: string): ShipTuning {
  const tuning = SHIP_WEAR_TUNING[asShipWearBand(wearBand)];
  if (asChargeBand(chargeBand) !== "dry") return tuning;
  return { ...tuning, maxSpeedFactor: 0, accelFactor: 0, climbFactor: 0 };
}
